import { useStores } from '../../stores';
import { useFormik } from 'formik';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Autocomplete } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Publisher } from '../../domain/Publisher';
import { useTranslation } from "react-i18next";
import { FieldServiceGroup } from '../../domain/FieldServiceGroup';

interface Props {
    isOpen: boolean;
    group?: FieldServiceGroup;
    onDelete: (id: number) => void;
    onClose: () => void;
}

interface FormValues {
    id: number;
    name: string;
    overseer: Publisher;
    assistant?: Publisher;
}

export default observer((props: Props) => {
    const { t } = useTranslation();
    const { publisherStore } = useStores();

    const initialValues = {} as FormValues;
    if (props.group) {
        initialValues.id = props.group.id;
        initialValues.name = props.group.name;
        initialValues.overseer = props.group.overseer;
        initialValues.assistant = props.group.assistant;
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        onSubmit: (values) => {
            publisherStore.updateServiceGroup(values.id, {
                name: values.name,
                overseer: values.overseer.id,
                assistant: values.assistant && values.assistant.id
            });
            props.onClose();
        },
    });

    return (
        <Dialog open={props.isOpen} fullWidth={true} onClose={props.onClose}>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>{t("update_group")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="normal"
                        id="name"
                        label={t("name")}
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formik.values.name || ''}
                        onChange={formik.handleChange}
                    />
                    <Autocomplete
                        disablePortal={false}
                        id="overseer"
                        options={publisherStore.publishers.filter(publisher => publisher.appointments.map(app => app.id).includes(1))}
                        getOptionLabel={(publisher: Publisher) => publisher.firstName ? `${publisher.firstName} ${publisher.lastName}` : ''}
                        value={formik.values.overseer || {} as Publisher}
                        renderInput={(params) => <TextField {...params} margin="normal" label={t("overseer")} />}
                        onChange={(e, overseer) => {
                            formik.setFieldValue("overseer", overseer)
                        }}
                    />
                    <Autocomplete
                        disablePortal={false}
                        id="assistant"
                        options={publisherStore.publishers.filter(publisher => {
                            const appointments = publisher.appointments.map(app => app.id);
                            return appointments.includes(1) || appointments.includes(2);
                        })}
                        getOptionLabel={(publisher: Publisher) => publisher.firstName ? `${publisher.firstName} ${publisher.lastName}` : ''}
                        value={formik.values.assistant || {} as Publisher}
                        renderInput={(params) => <TextField {...params} margin="normal" label={t("Assistant")} />}
                        onChange={(e, assistant) => {
                            formik.setFieldValue("assistant", assistant)
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button color="warning" onClick={() => props.onDelete(props.group!.id)}>{t("delete")}</Button>
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    <Button color="primary" type="submit">{t("submit")}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
});